/**
 * Which model a conversation's picker starts on — decided from what is known,
 * whatever order it arrives in.
 *
 * Three things load independently when a conversation opens: the model
 * catalog (scoped to the conversation since 2026-10-19, so usually cold when a
 * conversation is reopened), the conversation (its own model) and the agent
 * (its default and allowed models). The picker used to be set imperatively as
 * each arrived, and the last writer won: a reopened conversation that ran on
 * Gemini 3 Flash showed the agent's Sonnet, and the next turn silently ran on
 * Sonnet. Now the component re-derives the starting model from the facts each
 * time one changes, until the person picks a model themselves.
 *
 * The rule:
 *   1. The conversation's own model wins whenever there is one. Before the
 *      catalog can name its family it is kept as it is — never replaced by a
 *      default while it is unresolved. A model the catalog does not list is
 *      kept too (the server decides what runs). Only a model the agent does
 *      not allow falls through to rule 2.
 *   2. Otherwise the agent's default, when the agent allows it.
 *   3. Otherwise, for an agent with an allowed list, the tenant's default if
 *      the list holds it, else the list's first model.
 *   4. Otherwise the tenant's default.
 * Rules 2–4 need the catalog; before it arrives, with no conversation model,
 * the answer is '' (nothing to show yet).
 *
 * Pure and dependency-free so tests/unit/conversations/conversation_model.test.mjs
 * runs the real code; the catalog lookup is passed in.
 */

export interface StartingModelFacts {
  /**
   * The family key for a model ref (a family key, a pinned release or an
   * older release of it) — or null while the catalog has not loaded.
   * Returns undefined for a ref the catalog does not list.
   */
  resolve: ((ref: string) => string | undefined) | null;
  /** The conversation's own model: the server's effective model, else its current model. */
  conversationModel: string | null | undefined;
  /** The agent's default model (a ref). */
  agentDefault: string | null | undefined;
  /** The agent's allowed models (refs) — null when it allows every model. */
  allowed: readonly string[] | null | undefined;
  /** The tenant's default family, from the catalog. */
  tenantDefault: string | null | undefined;
}

export function startingModel(facts: StartingModelFacts): string {
  const { resolve } = facts;
  // An allowed list none of whose models the catalog lists restricts nothing —
  // the picker then shows every model, as ConversationPanel's filter does.
  const resolvedAllowed = resolve && facts.allowed && facts.allowed.length > 0
    ? facts.allowed.map(ref => resolve(ref)).filter((f): f is string => !!f)
    : [];
  const allowedFamilies = resolvedAllowed.length > 0 ? resolvedAllowed : null;
  const allows = (family: string) => !allowedFamilies || allowedFamilies.includes(family);

  if (facts.conversationModel) {
    if (!resolve) return facts.conversationModel;
    const family = resolve(facts.conversationModel);
    if (!family) return facts.conversationModel;
    if (allows(family)) return family;
  }
  if (!resolve) return '';

  const agentDefault = facts.agentDefault ? resolve(facts.agentDefault) : undefined;
  if (agentDefault && allows(agentDefault)) return agentDefault;

  const tenantDefault = facts.tenantDefault ? (resolve(facts.tenantDefault) || facts.tenantDefault) : '';
  if (allowedFamilies) {
    return tenantDefault && allowedFamilies.includes(tenantDefault) ? tenantDefault : allowedFamilies[0];
  }
  return tenantDefault;
}
